/**
 * Data validation helpers for the Indytend application
 * These functions help validate and recover data across the data flow
 */

// Validate event details structure
export const validateEventDetails = (eventDetails) => {
  if (!eventDetails || typeof eventDetails !== 'object') {
    return { isValid: false, errors: ['Event details is not an object'] };
  }

  const errors = [];
  const warnings = [];

  // Required fields
  if (!eventDetails.date) {
    errors.push('Missing event date');
  }
  if (!eventDetails.location) {
    errors.push('Missing event location');
  }
  if (!eventDetails.guestCount || eventDetails.guestCount < 1) {
    errors.push('Missing or invalid guest count');
  }

  // Optional but recommended fields
  if (!eventDetails.startTime) {
    warnings.push('Missing start time');
  }
  if (!eventDetails.endTime) {
    warnings.push('Missing end time');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    hasDate: !!eventDetails.date,
    hasLocation: !!eventDetails.location,
    hasGuestCount: !!eventDetails.guestCount,
    hasTimeRange: !!(eventDetails.startTime && eventDetails.endTime)
  };
};

// Validate cocktail selections structure
export const validateCocktailSelections = (cocktailSelections) => {
  if (!Array.isArray(cocktailSelections)) {
    return { isValid: false, errors: ['Cocktail selections is not an array'] };
  }

  if (cocktailSelections.length === 0) {
    return { isValid: false, errors: ['No cocktails selected'] };
  }

  const errors = [];
  const warnings = [];

  cocktailSelections.forEach((cocktail, index) => {
    if (!cocktail.name) {
      errors.push(`Cocktail ${index + 1}: Missing name`);
    }
    if (!cocktail.quantity && !cocktail.qty) {
      errors.push(`Cocktail ${index + 1}: Missing quantity`);
    }
    if (!cocktail.id) {
      warnings.push(`Cocktail ${index + 1}: Missing ID`);
    }
    if (!cocktail.ingredients || !Array.isArray(cocktail.ingredients)) {
      warnings.push(`Cocktail ${index + 1}: Missing or invalid ingredients`);
    }
  });

  const totalQuantity = cocktailSelections.reduce((sum, c) => {
    return sum + (c.quantity || c.qty || 0);
  }, 0);

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    count: cocktailSelections.length,
    totalQuantity,
    allHaveNames: cocktailSelections.every(c => c.name),
    allHaveQuantities: cocktailSelections.every(c => c.quantity || c.qty)
  };
};

// Validate pricing structure
export const validatePricing = (pricing) => {
  if (!pricing || typeof pricing !== 'object') {
    return { isValid: false, errors: ['Pricing is not an object'] };
  }

  const errors = [];
  const warnings = [];

  // Check for required pricing fields
  const requiredFields = ['totalIngredientCost', 'baseServiceCharge', 'totalServiceFees', 'grandTotal'];
  const missingFields = requiredFields.filter(field => typeof pricing[field] === 'undefined');
  
  if (missingFields.length > 0) {
    errors.push(`Missing required pricing fields: ${missingFields.join(', ')}`);
  }

  // Check for negative values
  Object.entries(pricing).forEach(([key, value]) => {
    if (typeof value === 'number' && value < 0) {
      warnings.push(`Negative value for ${key}: ${value}`);
    }
  });

  // Validate grand total calculation if all components are present
  if (typeof pricing.totalIngredientCost === 'number' && 
      typeof pricing.baseServiceCharge === 'number' && 
      typeof pricing.totalServiceFees === 'number' && 
      typeof pricing.grandTotal === 'number') {
    
    const calculatedTotal = pricing.totalIngredientCost + pricing.baseServiceCharge + pricing.totalServiceFees;
    const difference = Math.abs(calculatedTotal - pricing.grandTotal);
    
    if (difference > 0.01) { // Allow for small floating point differences
      warnings.push(`Grand total mismatch: calculated ${calculatedTotal}, stored ${pricing.grandTotal}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    hasRequiredFields: missingFields.length === 0,
    pricingKeys: Object.keys(pricing),
    totalAmount: pricing.grandTotal
  };
};

// Validate complete protectedData structure
export const validateProtectedData = (protectedData) => {
  if (!protectedData || typeof protectedData !== 'object') {
    return {
      isValid: false,
      errors: ['Protected data is not an object'],
      components: {}
    };
  }

  const components = {
    eventDetails: validateEventDetails(protectedData.eventDetails),
    cocktailSelections: validateCocktailSelections(protectedData.cocktailSelections || []),
    pricing: validatePricing(protectedData.pricing)
  };

  // Check for other add-ons
  if (protectedData.otherAddOns && Array.isArray(protectedData.otherAddOns)) {
    components.otherAddOns = {
      isValid: true,
      count: protectedData.otherAddOns.length,
      hasItems: protectedData.otherAddOns.length > 0
    };
  }

  // Check for add-on selections (ice, glassware)
  if (protectedData.addOnSelections && typeof protectedData.addOnSelections === 'object') {
    components.addOnSelections = {
      isValid: true,
      hasIce: !!protectedData.addOnSelections.ice,
      hasGlassware: !!protectedData.addOnSelections.glassware
    };
  }

  const allErrors = Object.values(components).flatMap(comp => comp.errors || []);
  const allWarnings = Object.values(components).flatMap(comp => comp.warnings || []);
  const isValid = Object.values(components).every(comp => comp.isValid);

  return {
    isValid,
    errors: allErrors,
    warnings: allWarnings,
    components,
    summary: {
      hasEventDetails: !!protectedData.eventDetails,
      hasCocktailSelections: !!(protectedData.cocktailSelections && protectedData.cocktailSelections.length > 0),
      hasPricing: !!protectedData.pricing,
      hasOtherAddOns: !!(protectedData.otherAddOns && protectedData.otherAddOns.length > 0),
      hasInquiryMessage: !!protectedData.inquiryMessage
    }
  };
};

// Attempt to recover missing or corrupted data
export const recoverProtectedData = (protectedData, fallbackSources = []) => {
  if (!protectedData) {
    protectedData = {};
  }

  const recovered = { ...protectedData };
  const recoveryLog = [];

  // Try to recover event details
  if (!recovered.eventDetails || !recovered.eventDetails.date) {
    for (const source of fallbackSources) {
      if (source.eventDate) {
        recovered.eventDetails = recovered.eventDetails || {};
        recovered.eventDetails.date = source.eventDate;
        recovered.eventDetails.startTime = source.eventTime || source.startTime;
        recovered.eventDetails.endTime = source.endTime;
        recovered.eventDetails.location = source.eventLocation || source.location;
        recovered.eventDetails.guestCount = source.guestCount;
        recoveryLog.push('Recovered event details from fallback source');
        break;
      }
    }
  }

  // Try to recover cocktail selections
  if (!recovered.cocktailSelections || recovered.cocktailSelections.length === 0) {
    for (const source of fallbackSources) {
      if (source.selectedItems?.cocktails && source.selectedItems.cocktails.length > 0) {
        recovered.cocktailSelections = source.selectedItems.cocktails.map(cocktail => ({
          ...cocktail,
          quantity: cocktail.quantity || cocktail.qty || 1
        }));
        recoveryLog.push('Recovered cocktail selections from fallback source');
        break;
      }
    }
  }

  // Try to recover pricing
  if (!recovered.pricing) {
    for (const source of fallbackSources) {
      if (source.pricing) {
        recovered.pricing = source.pricing;
        recoveryLog.push('Recovered pricing from fallback source');
        break;
      }
    }
  }

  return {
    data: recovered,
    recoveryLog,
    wasRecoveryNeeded: recoveryLog.length > 0
  };
};

// Session storage data validation
export const validateSessionStorageData = (sessionData) => {
  if (!sessionData || typeof sessionData !== 'object') {
    return { isValid: false, errors: ['Session data is not an object'] };
  }

  const errors = [];
  const warnings = [];

  // Check for required top-level properties
  if (!sessionData.orderData) {
    errors.push('Missing orderData');
  }
  if (!sessionData.listing) {
    errors.push('Missing listing');
  }

  // Validate orderData structure
  if (sessionData.orderData) {
    if (!sessionData.orderData.selectedItems) {
      warnings.push('Missing selectedItems in orderData');
    }
    if (!sessionData.orderData.eventDate && !sessionData.orderData.bookingDates) {
      warnings.push('Missing event date or booking dates');
    }
  }

  // Check data freshness
  if (sessionData.storedAt) {
    const storedTime = new Date(sessionData.storedAt);
    const now = new Date();
    const ageInHours = (now - storedTime) / (1000 * 60 * 60);
    
    if (ageInHours > 24) {
      warnings.push(`Session data is ${Math.round(ageInHours)} hours old`);
    }
  } else {
    warnings.push('Missing storage timestamp');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    hasOrderData: !!sessionData.orderData,
    hasListing: !!sessionData.listing,
    hasTransaction: !!sessionData.transaction,
    dataAge: sessionData.storedAt ? new Date() - new Date(sessionData.storedAt) : null
  };
};
